import { View, Text, StyleSheet, TextInput, TouchableOpacity, Alert, Platform } from 'react-native';
import { Trash2, CheckCircle } from 'lucide-react-native';
import { formatE1RM } from '@/lib/oneRepMax';
import type { SetData } from './ExerciseEntryCard';

interface SetRowProps {
  set: SetData;
  setIdx: number;
  isTimeBased: boolean;
  isFarmersCarry: boolean;
  isDeadHang?: boolean;
  userUnits: string;
  canRemove: boolean;
  showConfirm?: boolean;
  isPR: boolean;
  theme: any;
  displayWeight: (weight: number, fromUnits: string) => number;
  onUpdateSet: (patch: Partial<SetData>) => void;
  onRemoveSet: () => void;
  onConfirmSet?: () => void;
}

export default function SetRow({
  set,
  setIdx,
  isTimeBased,
  isFarmersCarry,
  isDeadHang,
  userUnits,
  canRemove,
  showConfirm = true,
  isPR,
  theme,
  displayWeight,
  onUpdateSet,
  onRemoveSet,
  onConfirmSet,
}: SetRowProps) {
  const e1rmText = (() => {
    const w = parseFloat(set.weight);
    const r = parseInt(set.reps);
    // Don't show Est. 1RM if weight is empty/zero or reps are invalid
    if (!set.weight || isNaN(w) || w <= 0 || isNaN(r) || r <= 0) return '';
    const convertedWeight = displayWeight(w, userUnits);
    return formatE1RM(convertedWeight, r, userUnits);
  })();

  return (
    <View>
      <View style={styles.setRow}>
        <Text style={[styles.setNumber, { color: theme.colors.textSecondary, width: 28 }]}>
          {setIdx + 1}
        </Text>
        {!isDeadHang && <TextInput
          style={[styles.setInput, {
            backgroundColor: theme.colors.surfaceVariant,
            borderColor: theme.colors.borderLight,
            color: theme.colors.text,
          }]}
          value={set.weight}
          onChangeText={(w) => onUpdateSet({ weight: w })}
          placeholder="0"
          placeholderTextColor={theme.colors.textTertiary}
          keyboardType="decimal-pad"
          accessibilityLabel={`Set ${setIdx + 1} weight`}
        />}
        <TextInput
          style={[styles.setInput, {
            backgroundColor: theme.colors.surfaceVariant,
            borderColor: theme.colors.borderLight,
            color: theme.colors.text,
          }]}
          value={isTimeBased ? (set.time || set.reps) : set.reps}
          onChangeText={(r) => isTimeBased ? onUpdateSet({ time: r, reps: r }) : onUpdateSet({ reps: r })}
          placeholder={isTimeBased ? 'sec' : '0'}
          placeholderTextColor={theme.colors.textTertiary}
          keyboardType="decimal-pad"
          accessibilityLabel={isTimeBased ? `Set ${setIdx + 1} time in seconds` : `Set ${setIdx + 1} reps`}
        />
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {canRemove && (
            <TouchableOpacity onPress={() => {
              if (Platform.OS === 'web') {
                if (window.confirm(`Remove set ${setIdx + 1}?`)) {
                  onRemoveSet();
                }
              } else {
                Alert.alert(
                  'Remove Set?',
                  `Remove set ${setIdx + 1}?`,
                  [
                    { text: 'Cancel', style: 'cancel' },
                    { text: 'Remove', style: 'destructive', onPress: onRemoveSet },
                  ]
                );
              }
            }} style={styles.removeSetBtn}
              accessibilityRole="button"
              accessibilityLabel={`Remove set ${setIdx + 1}`}
            >
              <Trash2 size={14} color={theme.colors.textTertiary} />
            </TouchableOpacity>
          )}
          {showConfirm && (
            <TouchableOpacity
              onPress={onConfirmSet}
              style={[styles.confirmSetBtn, {
                backgroundColor: theme.colors.accent + '20',
                borderColor: theme.colors.accent,
              }]}
              accessibilityRole="button"
              accessibilityLabel={`Confirm set ${setIdx + 1}`}
            >
              <CheckCircle size={16} color={theme.colors.accent} />
            </TouchableOpacity>
          )}
        </View>
      </View>
      {e1rmText ? (
        <Text style={[styles.e1rmText, { color: isPR ? theme.colors.accent : theme.colors.textTertiary }]}>
          {e1rmText}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  setRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  setNumber: {
    width: 20,
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  setInput: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 10,
    fontSize: 16,
    textAlign: 'center',
  },
  removeSetBtn: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmSetBtn: {
    minWidth: 44,
    minHeight: 44,
    borderRadius: 2,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  e1rmText: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'right',
    paddingRight: 36,
    marginBottom: 2,
    marginTop: -2,
  },
});
;
